import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { loginUser, registerUser, getMe, getFavourites, addFavourite, removeFavourite } from "../api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState([]);

  const loadFavs = useCallback(() => {
    getFavourites()
      .then((props) => setFavIds(props.map((p) => p.id)))
      .catch(() => setFavIds([]));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("anadear_token");
    if (token) {
      getMe()
        .then((data) => {
          setUser(data.user);
          loadFavs();
        })
        .catch(() => localStorage.removeItem("anadear_token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadFavs]);

  const login = useCallback(async (email, password) => {
    const data = await loginUser({ email: email.trim(), password: password.trim() });
    localStorage.setItem("anadear_token", data.token);
    setUser(data.user);
    loadFavs();
    return data.user;
  }, [loadFavs]);

  const register = useCallback(async (payload) => {
    const data = await registerUser(payload);
    localStorage.setItem("anadear_token", data.token);
    setUser(data.user);
    setFavIds([]);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("anadear_token");
    setUser(null);
    setFavIds([]);
  }, []);

  const isFavourite = useCallback((id) => favIds.includes(id), [favIds]);

  const toggleFavourite = useCallback(async (id) => {
    if (favIds.includes(id)) {
      setFavIds((f) => f.filter((x) => x !== id));
      try { await removeFavourite(id); } catch { loadFavs(); }
    } else {
      setFavIds((f) => [...f, id]);
      try { await addFavourite(id); } catch { loadFavs(); }
    }
  }, [favIds, loadFavs]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, favIds, isFavourite, toggleFavourite }}>
      {children}
    </AuthContext.Provider>
  );
};
